import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";

import {
  FILE_UPLOAD,
  CustomModalButtons,
} from "../../../constants/global.constants";
import { getSupportedFileTypes } from "../../../utils/appUtils";
import {
  excelIcon,
  successIcon,
  failureIcon,
  removeIcon,
  disabledRemoveIcon
} from "../../../assets";
import { ArrowRightIcon } from "../../../assets/icons";
import WPTertiaryButton from "../../../packages/WPTertiaryButton";
import SupportedFileInfo from "../SupportedFileInfo";
import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../../packages/ConfirmationPopup";

import "./FileDetailsCard.scss";
import ExportErrorIcon from "../../../packages/SVGs/ExportErrorIcon";

const FileDetailsCard = ({
  uploadedFile,
  setUploadedFile,
  onValidHandler,
  tempExistingCIPFileName,
  onAppendHandler,
  isUploadComplete,
  showDownload,
  appendStatus,
  showRemoveIcon,
  handleAppendReplace,
  handleRemoveFile,
  showAppend = false,
  showDeleteConfirmation = false,
}) => {
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [showRemoveFileConfirmation, setShowRemoveFileConfirmation] =
    useState(false);
  const fileInputRef = useRef();
  const { t } = useTranslation();

  const supportedFileTypes = getSupportedFileTypes(
    FILE_UPLOAD.SUPPORTED_FILE_TYPES,
    ".",
    true
  );

  const OnRemoveFileClick = () => {
    if (showDeleteConfirmation) {
      setShowRemoveFileConfirmation(true);
      return;
    }
    handleRemoveFile();
  };

  const onRemoveConfirmationClose = (id) => {
    setShowRemoveFileConfirmation(false);

    if (id === CustomModalButtons.CONFIRM) {
      handleRemoveFile();
    }
  };

  const delay = useCallback((ms) => new Promise((r) => setTimeout(r, ms)), []);

  const validateFile = useCallback(
    (file) => {
      return new Promise(async (resolve) => {
        setStatus("loading");
        setProgress(20);

        const fileExtension = file.name.split(".").pop().toLowerCase();
        const isValidType = FILE_UPLOAD.SUPPORTED_FILE_TYPES.some((type) =>
          type.toLowerCase().includes(fileExtension)
        );

        if (!isValidType) {
          await delay(400);
          setErrorMsg(t("fileUpload.invalidFileType"));
          setStatus("error");
          resolve(false);
          return;
        }

        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const workbook = XLSX.read(reader.result, { type: "array" });

            const allSheetsEmpty = workbook.SheetNames.every((sheetName) => {
              const sheet = workbook.Sheets[sheetName];
              const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
              return (
                data.length === 0 || (data.length === 1 && data[0].length === 0)
              );
            });

            if (allSheetsEmpty) {
              await delay(400);
              setErrorMsg(t("fileUpload.noData"));
              setStatus("error");
              resolve(false);
              return;
            }

            const maxSizeInBytes =
              parseInt(FILE_UPLOAD.MAX_FILE_SIZE) * 1024 * 1024;
            if (file.size > maxSizeInBytes) {
              setProgress(90);
              await delay(400);
              setErrorMsg(t("fileUpload.fileTooLarge"));
              setStatus("error");
              resolve(false);
              return;
            }

            await delay(400);
            setProgress(50);
            resolve(true);
          } catch (err) {
            setErrorMsg(t("fileUpload.invalidFileType"));
            setStatus("error");
            resolve(false);
          }
        };

        reader.onerror = () => {
          setErrorMsg(t("fileUpload.invalidFileType"));
          setStatus("error");
          resolve(false);
        };

        reader.readAsArrayBuffer(file);
      });
    },
    [t]
  );

  const handleReplaceClick = () => {
    if (appendStatus === "success") {
      handleAppendReplace();
      fileInputRef.current.click();
    } else {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const newFile = e.target.files[0];
    if (newFile) {
      setUploadedFile(newFile);
      e.target.value = "";
    }
  };

  const handleUploadClick = async () => {
    const isValid = await validateFile(uploadedFile);
    if (isValid) {
      const { error } = await onValidHandler();
      if (error) {
        setProgress(90);
        await delay(400);
        setErrorMsg(error);
        setStatus("error");
      } else {
        setProgress(100);
        await delay(400);
        setStatus("success");
      }
    }
  };

  useEffect(() => {
    if (uploadedFile || tempExistingCIPFileName) {
      setErrorMsg(null);
      setStatus("idle");
      setProgress(0);
    }
  }, [uploadedFile, tempExistingCIPFileName]);

  return (
    <div>
      <div className="ro-card-wrapper">
        <div className="file-details-card">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept={supportedFileTypes}
          />
          <img src={excelIcon} alt="Excel Icon" className="excel-icon" />
          <div className="file-info">
            <div className="file-name">
              {uploadedFile ? uploadedFile.name : tempExistingCIPFileName}
            </div>
            <div className="file-details">
              {status === "loading" && uploadedFile && (
                <div className="status-loader">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <span>{t("fileUpload.validating")}</span>
                </div>
              )}
              {status === "error" && (
                <span className="status-text error">
                  <img src={failureIcon} alt="Failure Icon" /> {errorMsg}
                </span>
              )}
              {(status === "success" || appendStatus === "success") && (
                <span className="status-text success">
                  <img src={successIcon} alt="Success Icon" />{" "}
                  {t("fileUpload.uploaded")}
                </span>
              )}
            </div>
          </div>
          <div className="actions">
            <WPTertiaryButton
              onClick={handleReplaceClick}
              disabled={status === "loading"}
              label={t("fileUpload.replace")}
            />
            {uploadedFile &&
              appendStatus !== "success" &&
              status !== "error" &&
              (!isUploadComplete && status !== "success" ? (
                <WPTertiaryButton
                  variant="contained"
                  onClick={handleUploadClick}
                  disabled={status === "loading"}
                  label={t("fileUpload.upload")}
                  icon={<ArrowRightIcon fill="white" />}
                  className="upload-button"
                />
              ) : (
                showAppend && (
                  <WPTertiaryButton
                    variant="contained"
                    onClick={onAppendHandler}
                    disabled={status === "loading"}
                    label={t("dataAppend.appendData")}
                    icon={<ExportErrorIcon />}
                    iconPlacement="before"
                    className="upload-button"
                  />
                )
              ))}
            {/* Keeping showremove icon temporarily because it is not being shown in Bulk yet */}
            {(uploadedFile || tempExistingCIPFileName) &&
              showRemoveIcon &&
              isUploadComplete && (
                <WPTertiaryButton
                  variant="text"
                  onClick={OnRemoveFileClick}
                  disabled={status === "loading"}
                  icon={
                    <img
                      src={
                        status === "loading" ? disabledRemoveIcon : removeIcon
                      }
                      alt="Remove Icon"
                    />
                  }
                  className="remove-button"
                />
              )}
            {showRemoveFileConfirmation && (
              <ConfirmationPopup
                header={t("fileUpload.removeFileHeader")}
                description={t("fileUpload.removeFileContent")}
                type={DIALOG_TYPES.ERROR}
                onClose={onRemoveConfirmationClose}
                confirmBtn={t("ValueStrings.remove")}
                cancelBtn={t("ValueStrings.cancel")}
              />
            )}
          </div>
        </div>
      </div>
      <SupportedFileInfo showDownload={showDownload} />
    </div>
  );
};

export default FileDetailsCard;
